import { SYNTH_INSTRUMENTS } from './synthInstruments.js'
import {
  SYNTH_FORMAT_VERSION,
  SYNTH_LAYER_COUNT,
  SYNTH_MAX_TRACKS,
  SYNTH_WAVEFORMS,
  createDefaultSynthResource,
  normalizeSynthResource,
} from './synthModel.js'

export const SYNTH_EVENTS_PER_CHUNK = 64
export const SYNTH_PPQ = 480

const TRACK_KEY_RE = /^track(\d+)\.(.+)$/
const LAYER_KEY_RE = /^layer(\d+)\.(.+)$/
const REQUIRED_TRACK_FIELDS = ['enabled', 'name', 'instrument', 'volume', 'adsr', 'vibrato', 'events']
const REQUIRED_LAYER_FIELDS = ['enabled', 'wave', 'pulse', 'volume', 'detune']

export function parseSynth(text) {
  return parseSynthFile(text).synth
}

export function parseSynthFile(text) {
  const synth = createDefaultSynthResource()
  const warnings = []
  const seen = new Set()
  const eventStreams = Array.from({ length: SYNTH_MAX_TRACKS }, () => [])

  for (const [lineIndex, sourceLine] of String(text ?? '').split(/\r?\n/).entries()) {
    const line = sourceLine.trim()
    if (!line || line.startsWith('#') || line.startsWith(';')) continue
    const appendIndex = line.indexOf('+=')
    const equalsIndex = line.indexOf('=')
    const append = appendIndex >= 0 && appendIndex === equalsIndex - 1
    const splitIndex = append ? appendIndex : equalsIndex
    if (splitIndex < 1) fail(lineIndex, '', 'Expected key=value')
    const key = line.slice(0, splitIndex).trim()
    const value = line.slice(splitIndex + (append ? 2 : 1)).trim()

    if (append) {
      const match = /^track(\d+)\.events$/.exec(key)
      if (!match) fail(lineIndex, key, 'Only track events support +=' )
      const trackIndex = parseTrackIndex(match[1], lineIndex, key)
      if (!seen.has(key)) fail(lineIndex, key, 'Event continuation requires a preceding events= line')
      eventStreams[trackIndex].push(...parseEvents(value, lineIndex, key))
      continue
    }

    if (seen.has(key)) fail(lineIndex, key, 'Duplicate property')
    seen.add(key)
    if (key === 'formatVersion') {
      if (parseInteger(value, lineIndex, key) !== SYNTH_FORMAT_VERSION) fail(lineIndex, key, `Unsupported synth formatVersion; expected ${SYNTH_FORMAT_VERSION}`)
      synth.formatVersion = SYNTH_FORMAT_VERSION
      continue
    }
    if (key === 'tempo.bpm') {
      synth.tempo.bpm = parseRange(value, 40, 240, lineIndex, key)
      continue
    }
    if (key === 'tempo.ppq') {
      if (parseInteger(value, lineIndex, key) !== SYNTH_PPQ) fail(lineIndex, key, `tempo.ppq must be ${SYNTH_PPQ}`)
      continue
    }
    const match = TRACK_KEY_RE.exec(key)
    if (!match) {
      warnings.push({ line: lineIndex + 1, key, code: 'unknown-property', message: `Unknown synth property: ${key}` })
      continue
    }
    const trackIndex = parseTrackIndex(match[1], lineIndex, key)
    assignTrackField(synth.tracks[trackIndex], trackIndex, match[2], value, eventStreams, warnings, lineIndex, key)
  }

  for (const key of ['formatVersion', 'tempo.bpm', 'tempo.ppq']) requireSeen(seen, key)
  for (let trackIndex = 0; trackIndex < SYNTH_MAX_TRACKS; trackIndex++) {
    for (const field of REQUIRED_TRACK_FIELDS) requireSeen(seen, `track${trackIndex}.${field}`)
    for (let layerIndex = 0; layerIndex < SYNTH_LAYER_COUNT; layerIndex++) {
      for (const field of REQUIRED_LAYER_FIELDS) requireSeen(seen, `track${trackIndex}.layer${layerIndex}.${field}`)
    }
    synth.tracks[trackIndex].sequence = eventStreams[trackIndex]
  }
  return { synth: normalizeSynthResource(synth), warnings }
}

export function serializeSynth(resource) {
  validateSynth(resource)
  const lines = ['# openHASP Synth', `formatVersion=${SYNTH_FORMAT_VERSION}`, `tempo.bpm=${formatNumber(resource.tempo.bpm)}`, `tempo.ppq=${SYNTH_PPQ}`]
  for (let trackIndex = 0; trackIndex < SYNTH_MAX_TRACKS; trackIndex++) {
    const track = resource.tracks[trackIndex]
    lines.push(`track${trackIndex}.enabled=${formatBoolean(track.enabled)}`)
    lines.push(`track${trackIndex}.name=${escapeValue(track.name)}`)
    lines.push(`track${trackIndex}.instrument=${track.instrument}`)
    lines.push(`track${trackIndex}.volume=${formatNumber(track.volume)}`)
    lines.push(`track${trackIndex}.adsr=${[track.adsr.attack, track.adsr.decay, track.adsr.sustain, track.adsr.release].map(formatNumber).join(',')}`)
    lines.push(`track${trackIndex}.vibrato=${formatBoolean(track.vibrato.enabled)},${formatNumber(track.vibrato.rateHz)},${formatNumber(track.vibrato.depthHz)}`)
    for (let layerIndex = 0; layerIndex < SYNTH_LAYER_COUNT; layerIndex++) {
      const layer = track.layers[layerIndex]
      const prefix = `track${trackIndex}.layer${layerIndex}`
      lines.push(`${prefix}.enabled=${formatBoolean(layer.enabled)}`)
      lines.push(`${prefix}.wave=${layer.waveform}`)
      lines.push(`${prefix}.pulse=${formatNumber(layer.pulseWidthPercent)}`)
      lines.push(`${prefix}.volume=${formatNumber(layer.volumePercent)}`)
      lines.push(`${prefix}.detune=${formatNumber(layer.detuneCents)}`)
    }
    const events = [...track.sequence].sort(compareEvents).map(event => `${event.startTick}:${event.note}:${event.durationTicks}`)
    if (!events.length) lines.push(`track${trackIndex}.events=`)
    for (let offset = 0; offset < events.length; offset += SYNTH_EVENTS_PER_CHUNK) {
      const operator = offset === 0 ? '=' : '+='
      lines.push(`track${trackIndex}.events${operator}${events.slice(offset, offset + SYNTH_EVENTS_PER_CHUNK).join(',')}`)
    }
  }
  return `${lines.join('\n')}\n`
}

function assignTrackField(track, trackIndex, field, value, eventStreams, warnings, lineIndex, key) {
  if (field === 'enabled') track.enabled = parseBoolean(value, lineIndex, key)
  else if (field === 'name') track.name = unescapeValue(value, lineIndex, key)
  else if (field === 'instrument') {
    if (!Object.hasOwn(SYNTH_INSTRUMENTS, value)) fail(lineIndex, key, `Unknown instrument: ${value}`)
    track.instrument = value
  } else if (field === 'volume') track.volume = parseRange(value, 0, 1, lineIndex, key)
  else if (field === 'adsr') track.adsr = parseAdsr(value, lineIndex, key)
  else if (field === 'vibrato') track.vibrato = parseVibrato(value, lineIndex, key)
  else if (field === 'events') eventStreams[trackIndex].push(...parseEvents(value, lineIndex, key))
  else {
    const match = LAYER_KEY_RE.exec(field)
    if (!match) {
      warnings.push({ line: lineIndex + 1, key, code: 'unknown-property', message: `Unknown track property: ${field}` })
      return
    }
    const layerIndex = Number(match[1])
    if (!Number.isInteger(layerIndex) || layerIndex < 0 || layerIndex >= SYNTH_LAYER_COUNT) fail(lineIndex, key, `Layer index is outside 0-${SYNTH_LAYER_COUNT - 1}`)
    const layer = track.layers[layerIndex]
    if (match[2] === 'enabled') layer.enabled = parseBoolean(value, lineIndex, key)
    else if (match[2] === 'wave') {
      if (!SYNTH_WAVEFORMS.includes(value)) fail(lineIndex, key, `Unknown waveform: ${value}`)
      layer.waveform = value
    } else if (match[2] === 'pulse') layer.pulseWidthPercent = parseRange(value, 0, 100, lineIndex, key)
    else if (match[2] === 'volume') layer.volumePercent = parseRange(value, 0, 100, lineIndex, key)
    else if (match[2] === 'detune') layer.detuneCents = parseRange(value, -1200, 1200, lineIndex, key)
    else warnings.push({ line: lineIndex + 1, key, code: 'unknown-property', message: `Unknown layer property: ${match[2]}` })
  }
}

function validateSynth(synth) {
  if (!synth || synth.formatVersion !== SYNTH_FORMAT_VERSION) throw new Error(`Synth formatVersion must be ${SYNTH_FORMAT_VERSION}.`)
  assertRange(synth.tempo?.bpm, 40, 240, 'tempo.bpm')
  if (!Array.isArray(synth.tracks) || synth.tracks.length !== SYNTH_MAX_TRACKS) throw new Error(`Synth must contain exactly ${SYNTH_MAX_TRACKS} tracks.`)
  synth.tracks.forEach((track, trackIndex) => {
    const prefix = `track${trackIndex}`
    if (typeof track.enabled !== 'boolean' || typeof track.name !== 'string') throw new Error(`${prefix} has invalid enabled or name metadata.`)
    if (!Object.hasOwn(SYNTH_INSTRUMENTS, track.instrument)) throw new Error(`${prefix}.instrument is invalid.`)
    assertRange(track.volume, 0, 1, `${prefix}.volume`)
    assertRange(track.adsr?.attack, 0, Infinity, `${prefix}.adsr.attack`)
    assertRange(track.adsr?.decay, 0, Infinity, `${prefix}.adsr.decay`)
    assertRange(track.adsr?.sustain, 0, 1, `${prefix}.adsr.sustain`)
    assertRange(track.adsr?.release, 0, Infinity, `${prefix}.adsr.release`)
    if (typeof track.vibrato?.enabled !== 'boolean') throw new Error(`${prefix}.vibrato.enabled must be boolean.`)
    assertRange(track.vibrato?.rateHz, 0, Infinity, `${prefix}.vibrato.rateHz`)
    assertRange(track.vibrato?.depthHz, 0, Infinity, `${prefix}.vibrato.depthHz`)
    if (!Array.isArray(track.layers) || track.layers.length !== SYNTH_LAYER_COUNT) throw new Error(`${prefix} must contain exactly ${SYNTH_LAYER_COUNT} layers.`)
    track.layers.forEach((layer, layerIndex) => {
      const layerPrefix = `${prefix}.layer${layerIndex}`
      if (typeof layer.enabled !== 'boolean' || !SYNTH_WAVEFORMS.includes(layer.waveform)) throw new Error(`${layerPrefix} has invalid enabled or waveform data.`)
      assertRange(layer.pulseWidthPercent, 0, 100, `${layerPrefix}.pulse`)
      assertRange(layer.volumePercent, 0, 100, `${layerPrefix}.volume`)
      assertRange(layer.detuneCents, -1200, 1200, `${layerPrefix}.detune`)
    })
    if (!Array.isArray(track.sequence)) throw new Error(`${prefix}.events must be an array.`)
    track.sequence.forEach((event, eventIndex) => validateEvent(event, `${prefix}.events[${eventIndex}]`))
  })
}

function parseEvents(value, lineIndex, key) {
  if (!value) return []
  return value.split(',').map(token => {
    const parts = token.trim().split(':')
    if (parts.length !== 3) fail(lineIndex, key, `Invalid event token: ${token}`)
    const event = { startTick: parseInteger(parts[0], lineIndex, key), note: parseInteger(parts[1], lineIndex, key), durationTicks: parseInteger(parts[2], lineIndex, key) }
    try { validateEvent(event, key) } catch (error) { fail(lineIndex, key, error.message) }
    return event
  })
}

function validateEvent(event, label) {
  if (!Number.isSafeInteger(event?.startTick) || event.startTick < 0) throw new Error(`${label}.startTick must be a non-negative safe integer.`)
  if (!Number.isInteger(event?.note) || event.note < 0 || event.note > 127) throw new Error(`${label}.note must be an integer from 0 through 127.`)
  if (!Number.isSafeInteger(event?.durationTicks) || event.durationTicks < 1) throw new Error(`${label}.durationTicks must be a positive safe integer.`)
}

function parseAdsr(value, lineIndex, key) {
  const values = parseNumberList(value, 4, lineIndex, key)
  values.slice(0, 2).concat(values[3]).forEach(number => assertParsedRange(number, 0, Infinity, lineIndex, key))
  assertParsedRange(values[2], 0, 1, lineIndex, key)
  return { attack: values[0], decay: values[1], sustain: values[2], release: values[3] }
}

function parseVibrato(value, lineIndex, key) {
  const parts = value.split(',').map(part => part.trim())
  if (parts.length !== 3) fail(lineIndex, key, 'Vibrato requires enabled, rateHz and depthHz')
  return { enabled: parseBoolean(parts[0], lineIndex, key), rateHz: parseRange(parts[1], 0, Infinity, lineIndex, key), depthHz: parseRange(parts[2], 0, Infinity, lineIndex, key) }
}

function parseNumberList(value, count, lineIndex, key) {
  const values = value.split(',').map(part => Number(part.trim()))
  if (values.length !== count || values.some(number => !Number.isFinite(number))) fail(lineIndex, key, `Expected ${count} finite numbers`)
  return values
}

function parseBoolean(value, lineIndex, key) {
  if (value !== '0' && value !== '1') fail(lineIndex, key, 'Boolean values must be 0 or 1')
  return value === '1'
}

function parseInteger(value, lineIndex, key) {
  if (!/^-?\d+$/.test(value) || !Number.isSafeInteger(Number(value))) fail(lineIndex, key, 'Expected a safe integer')
  return Number(value)
}

function parseRange(value, min, max, lineIndex, key) {
  const number = Number(value)
  assertParsedRange(number, min, max, lineIndex, key)
  return number
}

function assertParsedRange(value, min, max, lineIndex, key) {
  if (!Number.isFinite(value) || value < min || value > max) fail(lineIndex, key, `Value must be finite and between ${min} and ${max}`)
}

function assertRange(value, min, max, label) {
  if (!Number.isFinite(value) || value < min || value > max) throw new Error(`${label} must be finite and between ${min} and ${max}.`)
}

function parseTrackIndex(value, lineIndex, key) {
  const index = Number(value)
  if (!Number.isInteger(index) || index < 0 || index >= SYNTH_MAX_TRACKS) fail(lineIndex, key, `Track index is outside 0-${SYNTH_MAX_TRACKS - 1}`)
  return index
}

function requireSeen(seen, key) {
  if (!seen.has(key)) throw new Error(`Missing required synth property: ${key}.`)
}

function formatBoolean(value) { return value ? '1' : '0' }
function formatNumber(value) { return Number(Number(value).toFixed(6)).toString() }
function compareEvents(a, b) { return a.startTick - b.startTick || a.note - b.note || a.durationTicks - b.durationTicks }
function escapeValue(value) { return value.replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/\r/g, '\\r').replace(/=/g, '\\=') }
function unescapeValue(value, lineIndex, key) {
  let output = ''
  for (let index = 0; index < value.length; index++) {
    if (value[index] !== '\\') { output += value[index]; continue }
    const escaped = value[++index]
    if (escaped === '\\') output += '\\'
    else if (escaped === 'n') output += '\n'
    else if (escaped === 'r') output += '\r'
    else if (escaped === '=') output += '='
    else fail(lineIndex, key, `Unknown escape sequence: \\${escaped ?? ''}`)
  }
  return output
}
function fail(lineIndex, key, message) { throw new Error(`Invalid .synth v2 at line ${lineIndex + 1}${key ? ` (${key})` : ''}: ${message}.`) }
